import { motion } from "framer-motion";
import { ArrowRight, ArrowClockwise, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "../ui/button";

const PERSONAS = {
  MEASURED_BUILDER: {
    display_name: "Measured Builder",
    tagline: "Connect investment to a business outcome.",
    description:
      "Your answers suggest that you connect spending, internal ownership, and ongoing use to measurable business results.",
    recommendation:
      "A focused OnTenacity pilot with one workflow, a named owner, a baseline, and agreed success criteria.",
    before_starting:
      "Confirm that the opportunity is valuable enough to justify the full implementation effort.",
  },
  EVIDENCE_FIRST: {
    display_name: "Evidence-First Investor",
    tagline: "See relevant results before increasing commitment.",
    description:
      "Your answers suggest that practical demonstrations and direct comparisons play an important role in your investment decisions.",
    recommendation:
      "A bounded evaluation using representative examples from your company.",
    before_starting:
      "Agree on what the evaluation can demonstrate and what evidence would justify a broader pilot.",
  },
  BUDGET_FOCUSED: {
    display_name: "Budget-Focused Operator",
    tagline: "Fit the investment to current business priorities.",
    description:
      "Your answers suggest that available resources, ongoing costs, and the timing of returns strongly influence how you introduce technology.",
    recommendation:
      "One small use case with a clear spending limit and modest implementation requirements.",
    before_starting:
      "Include staff time and setup in the budget, and confirm that the trial is affordable without assuming immediate savings.",
  },
  GUIDED_IMPLEMENTER: {
    display_name: "Guided Implementer",
    tagline: "Invest in delivery expertise and support.",
    description:
      "Your answers suggest that provider involvement is an important part of making technology useful in your company.",
    recommendation:
      "A guided implementation assessment covering scope, responsibilities, support, and internal decisions.",
    before_starting:
      "Agree on what OnTenacity will provide and name someone in your company who can supply information and resolve decisions.",
  },
  TEAM_LED: {
    display_name: "Team-Led Adopter",
    tagline: "Build commitment through employee experience.",
    description:
      "Your answers suggest that employee feedback, practical use, and peer support influence how your company introduces and expands technology.",
    recommendation: "A trial with a small, willing team working on a task they want to improve.",
    before_starting:
      "Give the team learning time and agree on how their experience and business results will inform wider adoption.",
  },
  STAGED_INVESTOR: {
    display_name: "Staged Investor",
    tagline: "Commit progressively as the opportunity develops.",
    description:
      "Your answers suggest that you organize investment and implementation around successive stages, releasing resources as each stage earns the next.",
    recommendation: "An initial milestone within a broader implementation plan.",
    before_starting:
      "Define the first stage's budget and the evidence needed to expand, adjust, or stop.",
  },
};

const LIMITATION_NOTE =
  "This profile reflects your reported experience. Your current resources and chosen use case will determine the right scope.";

const PersonaCard = ({ personaKey, onStartJourney, showButton = true, testId }) => {
  const p = PERSONAS[personaKey];
  return (
    <div
      className="rounded-2xl p-6 md:p-8 bg-[#0F0F10] border border-white/10"
      data-testid={testId || `persona-card-${personaKey}`}
    >
      <h3 className="text-xs text-[#00E5FF] uppercase tracking-wider mb-2 font-medium">
        Your starting style
      </h3>
      <h2 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] mb-2">
        {p.display_name}
      </h2>
      <p className="text-sm text-[#00E5FF] font-medium mb-4">{p.tagline}</p>
      <p className="text-[#A1A1AA] leading-relaxed mb-6">{p.description}</p>

      <div className="mb-4 p-4 rounded-xl bg-white/5 border-l-2 border-[#00E5FF]">
        <div className="text-xs text-[#00E5FF] uppercase tracking-wider mb-1 font-medium">
          Recommended starting point
        </div>
        <div className="text-sm text-[#FAFAFA]">{p.recommendation}</div>
      </div>

      <div className="mb-6 p-4 rounded-xl bg-white/5 border-l-2 border-white/20">
        <div className="text-xs text-[#71717A] uppercase tracking-wider mb-1 font-medium">
          Before starting
        </div>
        <div className="text-sm text-[#FAFAFA]">{p.before_starting}</div>
      </div>

      {showButton && (
        <Button
          onClick={onStartJourney}
          className="btn-primary rounded-full px-8 py-3 flex items-center gap-2 group w-full sm:w-auto"
          data-testid={`start-journey-btn-${personaKey}`}
        >
          Start your AI journey
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </Button>
      )}
    </div>
  );
};

const EvidenceBlock = ({ result }) => {
  const evidence = buildEvidence(result);
  if (evidence.length === 0) return null;

  return (
    <div className="mt-6" data-testid="quiz-evidence">
      <h4 className="text-sm font-semibold text-white mb-3">What your answers highlighted</h4>
      <div className="space-y-2">
        {evidence.map((e, i) => (
          <div key={i} className="p-3 rounded-lg bg-white/5 border border-white/5">
            <div className="text-xs text-[#71717A] mb-1">You selected:</div>
            <div className="text-sm text-[#FAFAFA]">{e.answer_text}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

function buildEvidence(result) {
  const records = result.answer_records || [];
  const evidence = [];
  if (result.result_type === "BLENDED" && result.blended_personas) {
    for (const persona of result.blended_personas) {
      const match = records.find((r) => r.persona === persona);
      if (match) evidence.push(match);
    }
  } else if ((result.result_type === "PRIMARY" || result.result_type === "TENTATIVE") && result.primary_persona) {
    const primaryMatches = records.filter((r) => r.persona === result.primary_persona);
    evidence.push(...primaryMatches.slice(0, 2));
    if (result.supporting_persona && evidence.length >= 1) {
      const supMatch = records.find((r) => r.persona === result.supporting_persona);
      if (supMatch) {
        if (evidence.length === 1) evidence.push(supMatch);
        else evidence[1] = supMatch;
      }
    }
  }
  return evidence;
}

const QuizResult = ({ result, onStartJourney, onRetake, onReview }) => {
  const rt = result.result_type;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid={`quiz-result-${rt}`}
    >
      <div className="text-center mb-8">
        <span className="mono-label">YOUR RESULT</span>
      </div>

      {/* PRIMARY */}
      {rt === "PRIMARY" && (
        <>
          <PersonaCard personaKey={result.primary_persona} onStartJourney={onStartJourney} />
          <EvidenceBlock result={result} />

          {result.supporting_persona && (
            <div className="mt-6 p-5 rounded-xl bg-white/5 border border-white/10" data-testid="supporting-persona">
              <p className="text-sm text-[#A1A1AA] mb-2">
                <strong className="text-white">Also important to you:</strong>{" "}
                {PERSONAS[result.supporting_persona].tagline}
              </p>
              <div className="text-xs text-[#71717A] uppercase tracking-wider mb-1">
                Additional consideration
              </div>
              <p className="text-sm text-[#FAFAFA]">
                {PERSONAS[result.supporting_persona].before_starting}
              </p>
            </div>
          )}
        </>
      )}

      {/* BLENDED */}
      {rt === "BLENDED" && result.blended_personas && (
        <>
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
              Your starting style combines{" "}
              <span className="gradient-text">
                {PERSONAS[result.blended_personas[0]].display_name}
              </span>{" "}
              and{" "}
              <span className="gradient-text">
                {PERSONAS[result.blended_personas[1]].display_name}
              </span>
              .
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.blended_personas.map((p) => (
              <PersonaCard
                key={p}
                personaKey={p}
                onStartJourney={onStartJourney}
                testId={`blended-persona-${p}`}
              />
            ))}
          </div>

          <p className="mt-6 text-center text-sm text-[#A1A1AA]">
            <em>Shape an initial engagement around both priorities, using the starting approaches above.</em>
          </p>

          <EvidenceBlock result={result} />
        </>
      )}

      {/* TENTATIVE */}
      {rt === "TENTATIVE" && (
        <>
          <div className="mb-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] mb-3">
              Your answers lean toward{" "}
              <span className="gradient-text">
                {PERSONAS[result.primary_persona].display_name}
              </span>
              .
            </h2>
            <p className="text-sm text-[#A1A1AA] max-w-xl mx-auto">
              Your experience reflects several approaches. This is a useful starting suggestion
              rather than a fixed profile.
            </p>
          </div>
          <PersonaCard personaKey={result.primary_persona} onStartJourney={onStartJourney} />
          <EvidenceBlock result={result} />
        </>
      )}

      {/* FLEXIBLE */}
      {rt === "FLEXIBLE" && (
        <div className="rounded-2xl p-6 md:p-8 bg-[#0F0F10] border border-white/10">
          <h3 className="text-xs text-[#00E5FF] uppercase tracking-wider mb-2 font-medium">
            Your starting style
          </h3>
          <h2 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] mb-4">
            A flexible approach
          </h2>
          <p className="text-[#A1A1AA] leading-relaxed mb-6">
            Your answers reflect several ways of investing and introducing technology, without one
            recurring approach.
          </p>
          <div className="mb-6 p-4 rounded-xl bg-white/5 border-l-2 border-[#00E5FF]">
            <div className="text-xs text-[#00E5FF] uppercase tracking-wider mb-1 font-medium">
              Recommended starting point
            </div>
            <div className="text-sm text-[#FAFAFA]">
              Start by choosing one useful workflow and agreeing on the budget, support, and
              evidence you need for an initial evaluation.
            </div>
          </div>
          <Button
            onClick={onStartJourney}
            className="btn-primary rounded-full px-8 py-3 flex items-center gap-2 group"
            data-testid="start-journey-btn-flexible"
          >
            Start your AI journey
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      )}

      {/* INSUFFICIENT_HISTORY */}
      {rt === "INSUFFICIENT_HISTORY" && (
        <div className="rounded-2xl p-6 md:p-8 bg-[#0F0F10] border border-white/10">
          <h3 className="text-xs text-[#00E5FF] uppercase tracking-wider mb-2 font-medium">
            Your result
          </h3>
          <h2 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] mb-4">
            Let's shape your starting approach together
          </h2>
          <p className="text-[#A1A1AA] leading-relaxed mb-6">
            There isn't enough comparable experience in your answers to suggest a recurring
            investment style.
          </p>
          <div className="mb-6 p-4 rounded-xl bg-white/5 border-l-2 border-[#00E5FF]">
            <div className="text-xs text-[#00E5FF] uppercase tracking-wider mb-1 font-medium">
              Recommended starting point
            </div>
            <div className="text-sm text-[#FAFAFA]">
              A short discovery conversation can help identify a useful workflow and the
              resources needed to explore it.
            </div>
          </div>
          <Button
            onClick={onStartJourney}
            className="btn-primary rounded-full px-8 py-3 flex items-center gap-2 group"
            data-testid="start-journey-btn-insufficient"
          >
            Start your AI journey
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      )}

      {/* Unknown-count footnote */}
      {result.unknown_count > 0 && (
        <p className="mt-6 text-xs text-[#71717A] text-center">
          Based on {result.substantive_answers} of 5 questions with comparable experience.
        </p>
      )}

      {/* Limitation + actions */}
      <div className="mt-8 pt-6 border-t border-white/10">
        <p className="text-xs text-[#71717A] leading-relaxed mb-6">{LIMITATION_NOTE}</p>
        <div className="flex flex-wrap gap-4 items-center">
          <button
            onClick={onReview}
            className="text-sm text-[#A1A1AA] hover:text-white transition-colors flex items-center gap-1"
            data-testid="review-answers-btn"
          >
            <ArrowLeft size={14} /> Review my answers
          </button>
          <button
            onClick={onRetake}
            className="text-xs text-[#71717A] hover:text-white transition-colors flex items-center gap-1 ml-auto"
            data-testid="retake-quiz-btn"
          >
            <ArrowClockwise size={12} /> Retake quiz
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default QuizResult;
